import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    try {
      const stored = localStorage.getItem('userInfo');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  const [token, setToken] = useState(() => localStorage.getItem('token') || null);
  const [isLoading, setIsLoading] = useState(true);

  const role = user?.role ? user.role.toUpperCase() : null;
  const isAuthenticated = Boolean(token && user);

  // Sync token to storage
  const setSession = (newToken, newUser) => {
    if (newToken) {
      localStorage.setItem('token', newToken);
      setToken(newToken);
    } else {
      localStorage.removeItem('token');
      setToken(null);
    }

    if (newUser) {
      localStorage.setItem('userInfo', JSON.stringify(newUser));
      localStorage.setItem('doctorAuth', newUser.role === 'ADMIN' ? 'true' : 'false');
      setUser(newUser);
    } else {
      localStorage.removeItem('userInfo');
      localStorage.removeItem('doctorAuth');
      setUser(null);
    }
  };

  const logout = useCallback(async () => {
    try {
      if (token) {
        await api.post('/api/auth/logout');
      }
    } catch (e) {
      console.warn('Logout api failed or token already invalid', e);
    } finally {
      setSession(null, null);
    }
  }, [token]);

  // Handle unauthorized event from api interceptor
  useEffect(() => {
    const handleUnauthorized = () => {
      setSession(null, null);
    };
    window.addEventListener('auth:unauthorized', handleUnauthorized);
    return () => window.removeEventListener('auth:unauthorized', handleUnauthorized);
  }, []);

  // Verify token on mount
  useEffect(() => {
    const verifySession = async () => {
      const storedToken = localStorage.getItem('token');
      if (!storedToken) {
        setIsLoading(false);
        return;
      }

      try {
        const res = await api.get('/api/auth/me');
        if (res.data?.success && res.data?.data) {
          setSession(storedToken, res.data.data);
        } else {
          setSession(null, null);
        }
      } catch (err) {
        console.warn('Session verification failed:', err);
        setSession(null, null);
      } finally {
        setIsLoading(false);
      }
    };

    verifySession();
  }, []);

  const login = async (username, password) => {
    const response = await api.post('/api/auth/login', { username, password });
    if (response.data?.success) {
      const resData = response.data;
      const receivedToken = resData.token;
      const receivedUser = resData.user;
      setSession(receivedToken, receivedUser);
      return {
        success: true,
        message: resData.message,
        user: receivedUser,
        role: receivedUser.role.toUpperCase(),
      };
    }
    throw new Error(response.data?.message || 'Login failed');
  };

  const refreshUser = async () => {
    try {
      const res = await api.get('/api/auth/me');
      if (res.data?.success && res.data?.data) {
        setSession(token, res.data.data);
      }
    } catch (e) {
      console.warn('Failed to refresh user profile', e);
    }
  };

  const value = {
    user,
    token,
    role,
    isAuthenticated,
    isLoading,
    login,
    logout,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
